import React, { useState, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import chillLeeImage from '../../assets/images/thumbnails/chillLee.png';
import beachclubleeImage from '../../assets/images/thumbnails/beachclublee.webp';
import convientLeeImage from '../../assets/images/thumbnails/convinentlee.webp';

const MinimalCarousel = ({
  items = [],
  autoPlay = true,
  autoPlayInterval = 5000,
  className = '',
  bottomSpacing = 24 // New prop to control bottom spacing
}) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const defaultItems = [
    {
      id: 1,
      title: t('zones.titleAcQuiet'),
      description: t('zones.defaultDesc'),
      image: chillLeeImage
    },
    {
      id: 2,
      title: t('zones.titleKidFriendly'),
      description: t('zones.defaultDesc'),
      image: beachclubleeImage
    },
    {
      id: 3,
      title: t('zones.titleOffers'),
      description: t('zones.defaultDesc'),
      image: convientLeeImage
    }
  ];

  const displayItems = items.length > 0 ? items : defaultItems;

  useEffect(() => {
    if (!autoPlay || displayItems.length <= 1 || isHovered) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev === displayItems.length - 1 ? 0 : prev + 1));
    }, autoPlayInterval);
    return () => clearInterval(interval);
  }, [autoPlay, autoPlayInterval, displayItems.length, isHovered]);

  // Trigger animation when slide changes
  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 800);
    return () => clearTimeout(timer);
  }, [currentIndex]);

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? displayItems.length - 1 : currentIndex - 1);
  };
  
  const goToNext = () => {
    setCurrentIndex(currentIndex === displayItems.length - 1 ? 0 : currentIndex + 1);
  };

  if (displayItems.length === 0) return null;

  return (
    <>
      <style>
        {`
          @keyframes slideInFromBottomRight {
            0% {
              transform: translate(60px, 60px);
              opacity: 0;
            }
            50% {
              transform: translate(30px, 30px);
              opacity: 0.3;
            }
            100% {
              transform: translate(0, 0);
              opacity: 1;
            }
          }

          @keyframes slideInFromBottomRightTitle {
            0% {
              transform: translate(80px, 40px);
              opacity: 0;
            }
            100% {
              transform: translate(0, 0);
              opacity: 1;
            }
          }

          @keyframes slideInFromBottomRightDescription {
            0% {
              transform: translate(100px, 60px);
              opacity: 0;
            }
            100% {
              transform: translate(0, 0);
              opacity: 1;
            }
          }

          @keyframes fadeInScale {
            0% {
              transform: scale(0.95);
              opacity: 0;
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }

          @keyframes pulseGlow {
            0%, 100% {
              box-shadow: 0 2px 8px rgba(0,0,0,0.12);
            }
            50% {
              box-shadow: 0 4px 16px rgba(0,0,0,0.2);
            }
          }

          @keyframes bounceIn {
            0% {
              transform: translateY(-50%) scale(0.8);
              opacity: 0;
            }
            60% {
              transform: translateY(-50%) scale(1.05);
              opacity: 1;
            }
            100% {
              transform: translateY(-50%) scale(1);
              opacity: 1;
            }
          }

          .carousel-text-container {
            animation: slideInFromBottomRight 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          }

          .carousel-text-container.animating .carousel-title {
            animation: slideInFromBottomRightTitle 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          }

          .carousel-text-container.animating .carousel-description {
            animation: slideInFromBottomRightDescription 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.15s both;
          }

          .carousel-nav-button {
            transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          }

          .carousel-nav-button:hover {
            background: rgba(20,20,20,0.95);
            animation: pulseGlow 2s ease-in-out infinite;
            transform: translateY(-50%) scale(1.1);
          }

          .carousel-nav-button:active {
            transform: translateY(-50%) scale(0.95);
            animation: bounceIn 0.3s ease-out;
          }

          .carousel-indicator {
            transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          }

          .carousel-indicator.active {
            animation: fadeInScale 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          }

          .image-background {
            transition: transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          }

          .minimal-carousel:hover .image-background {
            transform: scale(1.03);
          }

          .carousel-slide-container {
            transition: opacity 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          }
        `}
      </style>
      
      <div className={`minimal-carousel ${className}`} style={{ 
        marginBottom: '32px', // Increased bottom margin
        padding: '20px 20px 28px 20px', // Added bottom padding
      }}>
        {/* Section Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px', // Increased bottom margin
          padding: '0 4px'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#1f2937',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            {t('zones.sectionTitle')}
          </h3>
          <div style={{
            fontSize: '12px',
            color: '#6b7280',
            backgroundColor: '#f3f4f6',
            padding: '4px 8px',
            borderRadius: '12px'
          }}>
            {t('zones.zonesCount', { count: displayItems.length })}
          </div>
        </div>

        {/* Carousel Container with Bottom Spacing */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '240px', // Increased height
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            marginBottom: '16px' // Added bottom margin to carousel
          }}
        >
          {displayItems.map((item, idx) => (
            <div
              key={`${item.id}-${idx}`}
              className="carousel-slide-container"
              style={{
                display: idx === currentIndex ? 'block' : 'none',
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                opacity: idx === currentIndex ? 1 : 0
              }}
            >
              {/* Image background */}
              <div
                className="image-background"
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundImage: `url(${item.image})`,
                  backgroundPosition: 'center',
                  backgroundSize: 'cover',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  zIndex: 1
                }}
              />
              
              {/* Enhanced Gradient Overlay for better text readability */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 2,
                  background: 'linear-gradient(45deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.2) 30%, rgba(0,0,0,0.4) 60%, rgba(0,0,0,0.8) 100%)'
                }}
              />
              
              {/* Text Container - Positioned at Bottom Right with Animation */}
              <div
                className={`carousel-text-container ${isAnimating ? 'animating' : ''}`}
                style={{
                  position: 'absolute',
                  bottom: '28px',
                  right: '28px',
                  zIndex: 3,
                  maxWidth: '420px',
                  minWidth: '300px',
                  textAlign: 'right',
                  color: '#fff',
                  textShadow: '0 3px 15px rgba(0,0,0,0.6)',
                  padding: '8px 0'
                }}
              >
                <h2 
                  className="carousel-title"
                  style={{
                    fontSize: '2.1rem',
                    fontWeight: 700,
                    marginBottom: '14px',
                    lineHeight: '1.2',
                    letterSpacing: '-0.5px',
                    textShadow: '0 2px 8px rgba(0,0,0,0.7)'
                  }}
                >
                  {item.title}
                </h2>
                <p 
                  className="carousel-description"
                  style={{
                    fontSize: '1.15rem',
                    lineHeight: '1.5',
                    fontWeight: 400,
                    opacity: 0.96,
                    textShadow: '0 2px 6px rgba(0,0,0,0.5)'
                  }}
                >
                  {item.description}
                </p>
              </div>
            </div>
          ))}

          {/* Enhanced Navigation Buttons */}
          {displayItems.length > 1 && (
            <>
              <button
                aria-label={t('common.previousSlide')}
                onClick={goToPrevious}
                className="carousel-nav-button"
                style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  borderRadius: '50%',
                  border: 'none',
                  width: '44px',
                  height: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 5,
                  boxShadow: '0 3px 12px rgba(0,0,0,0.15)',
                  cursor: 'pointer',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <ChevronLeftIcon style={{ width: 24, height: 24, color: '#fff' }} />
              </button>
              
              <button
                aria-label={t('common.nextSlide')}
                onClick={goToNext}
                className="carousel-nav-button"
                style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  borderRadius: '50%',
                  border: 'none',
                  width: '44px',
                  height: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 5,
                  boxShadow: '0 3px 12px rgba(0,0,0,0.15)',
                  cursor: 'pointer',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <ChevronRightIcon style={{ width: 24, height: 24, color: '#fff' }} />
              </button>
            </>
          )}

          {/* Enhanced Indicators with Bottom Spacing */}
          {displayItems.length > 1 && (
            <div
              style={{
                position: 'absolute',
                bottom: '24px', // Increased from 20px to 24px
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: '10px',
                zIndex: 6,
                paddingBottom: '8px' // Added extra bottom padding
              }}
            >
              {displayItems.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`carousel-indicator ${idx === currentIndex ? 'active' : ''}`}
                  style={{
                    width: idx === currentIndex ? '28px' : '12px',
                    height: '12px',
                    borderRadius: '6px',
                    background: idx === currentIndex ? '#fff' : 'rgba(255, 255, 255, 0.45)',
                    border: 'none',
                    cursor: 'pointer',
                    opacity: idx === currentIndex ? 1 : 0.75,
                    boxShadow: idx === currentIndex ? '0 2px 8px rgba(255,255,255,0.3)' : 'none'
                  }}
                  aria-label={t('common.goToSlide', { number: idx + 1 })}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MinimalCarousel;
